/**
 * P2 链路测试台运行 API 客户端。
 *
 * 这里刻意接真实 runtime/admin endpoint，而不是在页面里继续静态模拟：
 * - /api/v1/messages：完整 Agent 主链路 + trace 写入
 * - /api/v1/intent：单独意图识别
 * - /api/v1/dispatch：意图 + 方案推荐
 * - /admin/v1/skills 与 /admin/v1/knowledge：工具/知识沙盒自检
 */
import type { components } from "../../../../../packages/shared/src/api-types";

import { apiClient } from "@/lib/api-client";
import type { KnowledgeSearchSandboxResponse } from "@/lib/api/knowledge";
import type { OAuthHealthResponse, SkillSandboxResponse } from "@/lib/api/skills";

type ApiSchemas = components["schemas"];
type AdminRunEventSchema = ApiSchemas["AdminRunEvent"];
type AdminRunRequestSchema = ApiSchemas["AdminRunRequest"];
type MessagesRequestSchema = ApiSchemas["MessagesRequest"];

export interface PipelineIntentResponse {
	intent: string;
	service_stage: string;
	dialogue_signal: Record<string, unknown>;
	intent_continuity: "continue" | "switch";
	confidence: number;
	topic: string | null;
	secondary_intent: string | null;
	sub_task?: string | null;
	dialogue_state?: string | null;
	missing_info?: string[];
	inferred_focused_product?: string | null;
	inferred_focused_order?: string | null;
	reasoning?: string | null;
	implementation_level: string;
	l2_matched_rule_id: string | null;
}

export interface PipelineRecommendedPlaybook {
	id: string;
	name: string;
	guidance: string;
	fallback_hint: string;
	version: string;
}

export interface PipelinePhaseTraceEntry {
	phase: string;
	status: string;
	elapsed_ms: number;
	summary: Record<string, unknown>;
}

export interface PipelineRuntimeCapabilities {
	phase4_runner_mode:
		| "static"
		| "llm"
		| "llm_fallback"
		| "sdk_loop"
		| "sdk_loop_fallback"
		| "custom";
	prompt_effect_mode: "rendered_not_consumed" | "consumed_by_llm" | "preview_only" | "unknown";
	rag_mode: "null" | "configured" | "qdrant_live" | "error";
	session_mode: "request_only" | "pg_session" | "redis_pg_session";
	quality_mode:
		| "q_fast_grounding_starter"
		| "q_fast_grounding_side_effect"
		| "q_deep_side_effect"
		| "custom";
	handoff_mode: "placeholder" | "contract_only" | "ticket_created" | "agent_connected" | "disabled";
	shopify_read_mode: "not_requested" | "live_read" | "failed_or_blocked";
	shopify_write_mode: "disabled" | "dry_run" | "live_confirmed" | "blocked";
}

export interface PipelineMessageResponse {
	trace_id: string;
	session_id: string;
	intent: PipelineIntentResponse;
	recommended_playbook: PipelineRecommendedPlaybook | null;
	recommended_skills: string[];
	universal_skills: string[];
	match_mode:
		| "routing_table"
		| "short_circuit"
		| "config_mapping"
		| "vector"
		| "no_match"
		| "skipped"
		| "tag_match";
	selected_branch?: string | null;
	selected_branch_display_name?: string | null;
	selected_branch_source?: "routing_table" | "sub_task" | "confidence_gate" | null;
	short_circuit_skill?: string | null;
	routing_rule_id?: string | null;
	reply_text: string;
	phase_trace: PipelinePhaseTraceEntry[];
	runtime_capabilities: PipelineRuntimeCapabilities;
	reply_placeholder: string;
	final_action_type: "inform" | "clarify" | "commit_action" | "handoff";
	final_outcome: "success" | "partial" | "failed" | "handoff";
	history_turns_included: number;
}

export interface PipelineDispatchResponse {
	intent: PipelineIntentResponse;
	recommended_playbook: PipelineRecommendedPlaybook | null;
	recommended_skills: string[];
	universal_skills: string[];
	match_mode:
		| "routing_table"
		| "short_circuit"
		| "config_mapping"
		| "vector"
		| "no_match"
		| "skipped"
		| "tag_match";
	selected_branch?: string | null;
	short_circuit_skill?: string | null;
	routing_rule_id?: string | null;
}

export interface PipelineMessageRequest {
	merchant_id: string;
	session_id?: string | null;
	customer_id?: MessagesRequestSchema["customer_id"];
	message: string;
	history?: Array<{ role: "user" | "agent"; content: string }>;
	previous_intent?: string | null;
	current_playbook_id?: string | null;
}

export type PipelineIntentRequest = Pick<
	PipelineMessageRequest,
	"message" | "history" | "previous_intent"
>;

export type PipelineAdminRunEventStatus = AdminRunEventSchema["status"];
export type PipelineAdminRunStageId = AdminRunEventSchema["stage_id"];
export type PipelineAdminRunTimingSource = AdminRunEventSchema["timing_source"];
export type PipelineAdminRunEventType = AdminRunEventSchema["event_type"];

export interface PipelineAdminRunEvent {
	run_id: string;
	trace_id: string | null;
	session_id: string | null;
	event_id: string;
	event_type: PipelineAdminRunEventType;
	stage_id: PipelineAdminRunStageId;
	status: PipelineAdminRunEventStatus;
	title: string;
	summary: string;
	input: Record<string, unknown>;
	decision: Record<string, unknown>;
	output: Record<string, unknown>;
	evidence_refs: string[];
	field_refs: string[];
	raw: Record<string, unknown>;
	duration_ms?: number | null;
	offset_ms?: number | null;
	timing_source?: PipelineAdminRunTimingSource;
}

export interface PipelineAdminRunRequest extends PipelineMessageRequest {
	customer_id?: AdminRunRequestSchema["customer_id"];
	data_mode?: AdminRunRequestSchema["data_mode"];
}

export interface PipelineAdminRunResponse {
	run_id: string;
	trace_id: string;
	session_id: string;
	status: "running" | "completed" | "failed";
	final_action_type: string;
	final_outcome: string;
	final_reply: string;
	total_latency_ms: number;
	events: PipelineAdminRunEvent[];
	messages_response: PipelineMessageResponse;
}

export interface PipelineAdminRunEventsResponse {
	run_id: string;
	trace_id: string;
	session_id: string;
	status: "running" | "completed" | "failed";
	items: PipelineAdminRunEvent[];
}

export type PipelineAdminRunStreamItem =
	| { type: "event"; item: PipelineAdminRunEvent }
	| { type: "completed"; run: PipelineAdminRunResponse }
	| { type: "error"; status_code: number; detail: unknown };

export async function runPipelineMessage(body: PipelineMessageRequest) {
	const { data } = await apiClient.post<PipelineMessageResponse>("/api/v1/messages", {
		merchant_id: body.merchant_id,
		session_id: body.session_id ?? null,
		customer_id: body.customer_id ?? null,
		message: body.message,
		history: body.history ?? [],
		previous_intent: body.previous_intent ?? null,
		current_playbook_id: body.current_playbook_id ?? null,
	});
	return data;
}

export async function runPipelineAdminRun(body: PipelineAdminRunRequest) {
	const { data } = await apiClient.post<PipelineAdminRunResponse>("/admin/v1/runs", {
		merchant_id: body.merchant_id,
		session_id: body.session_id ?? null,
		customer_id: body.customer_id ?? null,
		message: body.message,
		history: body.history ?? [],
		previous_intent: body.previous_intent ?? null,
		current_playbook_id: body.current_playbook_id ?? null,
		data_mode: body.data_mode ?? null,
	});
	return data;
}

export async function runPipelineAdminRunStream(
	body: PipelineAdminRunRequest,
	handlers: { onEvent: (event: PipelineAdminRunEvent) => void },
) {
	const response = await fetch(buildApiUrl("/admin/v1/runs/stream"), {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			merchant_id: body.merchant_id,
			session_id: body.session_id ?? null,
			customer_id: body.customer_id ?? null,
			message: body.message,
			history: body.history ?? [],
			previous_intent: body.previous_intent ?? null,
			current_playbook_id: body.current_playbook_id ?? null,
			data_mode: body.data_mode ?? null,
		}),
	});
	if (!response.ok) {
		throw new Error(`Admin run stream failed: ${response.status}`);
	}
	if (!response.body) {
		throw new Error("Admin run stream did not return a readable body");
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let completedRun: PipelineAdminRunResponse | null = null;

	for (;;) {
		const { value, done } = await reader.read();
		buffer += decoder.decode(value, { stream: !done });
		const lines = buffer.split("\n");
		buffer = lines.pop() ?? "";
		for (const line of lines) {
			const item = parseStreamItem(line);
			if (!item) continue;
			if (item.type === "event") {
				handlers.onEvent(item.item);
			} else if (item.type === "completed") {
				completedRun = item.run;
			} else {
				throw new Error(`Admin run stream error: ${String(item.detail)}`);
			}
		}
		if (done) break;
	}
	const tail = parseStreamItem(buffer);
	if (tail?.type === "event") {
		handlers.onEvent(tail.item);
	} else if (tail?.type === "completed") {
		completedRun = tail.run;
	} else if (tail?.type === "error") {
		throw new Error(`Admin run stream error: ${String(tail.detail)}`);
	}
	if (!completedRun) {
		throw new Error("Admin run stream ended before completion");
	}
	return completedRun;
}

function parseStreamItem(line: string): PipelineAdminRunStreamItem | null {
	const trimmed = line.trim();
	if (!trimmed) return null;
	return JSON.parse(trimmed) as PipelineAdminRunStreamItem;
}

function buildApiUrl(path: string): string {
	const baseURL = apiClient.defaults.baseURL ?? "";
	if (!baseURL) return path;
	return `${baseURL.replace(/\/$/, "")}${path}`;
}

export async function fetchPipelineAdminRunEvents(runId: string) {
	const { data } = await apiClient.get<PipelineAdminRunEventsResponse>(
		`/admin/v1/runs/${encodeURIComponent(runId)}/events`,
	);
	return data;
}

export async function runPipelineIntent(body: string | PipelineIntentRequest) {
	const request = typeof body === "string" ? { message: body } : body;
	const { data } = await apiClient.post<PipelineIntentResponse>("/api/v1/intent", {
		message: request.message,
		history: request.history ?? [],
		previous_intent: request.previous_intent ?? null,
	});
	return data;
}

export async function runPipelineDispatch(body: PipelineMessageRequest) {
	const { data } = await apiClient.post<PipelineDispatchResponse>("/api/v1/dispatch", {
		message: body.message,
		history: body.history ?? [],
		previous_intent: body.previous_intent ?? null,
		current_playbook_id: body.current_playbook_id ?? null,
		merchant_id: body.merchant_id,
	});
	return data;
}

export async function runPipelineOAuthHealth(merchantId: string) {
	const { data } = await apiClient.get<OAuthHealthResponse>("/admin/v1/skills/oauth-health", {
		params: { merchant_id: merchantId, probe: true },
	});
	return data;
}

export async function runPipelineSkillSandbox(merchantId: string, message: string) {
	const { data } = await apiClient.post<SkillSandboxResponse>(
		"/admin/v1/skills/skills/SK05/sandbox",
		{
			merchant_id: merchantId,
			session_id: "p2_pipeline_tool_check",
			extracted_slots: { query: message, user_message: message },
			rag_namespaces: ["policy", "faq"],
			top_k: 5,
		},
	);
	return data;
}

export async function runPipelineKnowledgeSandbox(merchantId: string, message: string) {
	const { data } = await apiClient.post<KnowledgeSearchSandboxResponse>(
		"/admin/v1/knowledge/search-sandbox",
		{
			merchant_id: merchantId,
			query: message,
			namespaces: ["policy", "faq"],
			top_k: 5,
		},
	);
	return data;
}
